import { useCallback, useEffect, useRef, useState } from 'react';

import { devError } from '../utils';

const useControlledState = <T>(controlled: T, defaultProp: T, name: string, state = 'value') => {
    // isControlled is ignored in the hook dependency lists as it should never change.
    const { current: isControlled } = useRef(controlled !== undefined);
    const [valueState, setValue] = useState(defaultProp);
    const value = isControlled ? controlled : valueState;

    if (process.env.NODE_ENV !== 'production') {
        useEffect(() => {
            if (isControlled !== (controlled !== undefined)) {
                devError(
                    [
                        `A component is changing the ${
                            isControlled ? '' : 'un'
                        }controlled ${state} state of ${name} to be ${isControlled ? 'un' : ''}controlled.`,
                        'Elements should not switch from uncontrolled to controlled (or vice versa).',
                        `Decide between using a controlled or uncontrolled ${name} ` +
                            'element for the lifetime of the component.',
                        "The nature of the state is determined during the first render. It's considered controlled if the value is not `undefined`.",
                        'More info: https://react.dev/reference/react-dom/components/input#im-getting-an-error-a-component-is-changing-an-uncontrolled-input-to-be-controlled'
                    ].join('\n')
                );
            }
        }, [state, name, controlled]);

        const { current: defaultValue } = useRef(defaultProp);

        useEffect(() => {
            if (!isControlled && defaultValue !== defaultProp) {
                devError(
                    [
                        `A component is changing the default ${state} state of an uncontrolled ${name} after being initialized. ` +
                            `To suppress this warning opt to use a controlled ${name}.`
                    ].join('\n')
                );
            }
        }, [JSON.stringify(defaultProp)]);
    }

    const setValueIfUncontrolled = useCallback((newValue: T) => {
        if (!isControlled) {
            setValue(newValue);
        }
    }, []);

    return [value, setValueIfUncontrolled] as const;
};

export default useControlledState;
